import { IntegrationAppProvider } from "@membranehq/react";
import { useEffect } from "react";
import SettingsLayout from "./SettingsLayout";
import {
  useWorkspaceMembers,
  useWorkspaceObjectSchema,
  useWorkspacePreferences,
} from "../../../hooks/QueryHooks";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import { generateIntegrationAppToken } from "../../../services/integrationAppEndpoint";
import { WorkspacePreferenceResponse } from "../../../services/preference/preferenceEndpoints.interface";
import usePreferencesStore from "../../../store/preference/preferencesStore";
import GlobalSpotlight from "../../../shared/spotlight/GlobalSpotlight";

const SettingsPage = () => {
  const { data: workspace, isLoading } = useSelectWorkspace();

  useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    retry: 2,
  });

  useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const preferences = useWorkspacePreferences({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const setPreferences = usePreferencesStore((state) => state.setPreferences);
  const setWorkspaceId = usePreferencesStore((state) => state.setWorkspaceId);

  useEffect(() => {
    if (workspace?.id) {
      setWorkspaceId(workspace.id);
    }

    const preferencesData = preferences.data as WorkspacePreferenceResponse;
    if (
      preferencesData
      && preferencesData.accounts
      && preferencesData.users
      && preferencesData.id
    ) {
      setPreferences(preferencesData);
    }
  }, [workspace?.id, preferences.data, setPreferences, setWorkspaceId]);

  if (!workspace?.id) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading...</div>;
  }

  const fetchToken = async () => {
    try {
      const response = await generateIntegrationAppToken({
        workspace_id: workspace.id,
        isMemberLevel: false,
      });
      return response?.data?.token;
    } catch (error) {
      console.error("Failed to fetch integration token:", error);
      return null;
    }
  };

  const isPageLoading = isLoading || preferences.isLoading;

  return (
    <IntegrationAppProvider fetchToken={fetchToken}>
      <div className="h-screen bg-background">
        <GlobalSpotlight />
        <div className="relative h-full">
          {isPageLoading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
              <span className="h-7 w-7 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
            </div>
          )}
          <SettingsLayout />
        </div>
      </div>
    </IntegrationAppProvider>
  );
};

export default SettingsPage;
